import React from 'react'
import { ActivityIndicator, Text, View } from 'react-native'
import { Tab, Tabs } from '@material-ui/core'
import { assets, exportCSV, PolicyDispatchingPermission } from '@pulseops/common'
import { StackScreenProps } from '@react-navigation/stack'
import { pipe } from 'fp-ts/function'
import { ZIO } from '@mxt/zio'
import { PolicyDispatchingStackParamList } from './PolicyDispatchingStackParamList'
import PolicyDispatchingDialog from './components/PolicyDispatchingDialog'
import { PolicyDispatchingService } from './services/PolicyDispatchingService'
import { getPermissions } from './utils/policyDispatchingUtils'

type Props = StackScreenProps<PolicyDispatchingStackParamList, 'PDUploadScreen'>

const STOCK_IN_TEMPLATE_HEADERS = ['#poli_no', 'poli_agent1', 'poli_agent2', 'poli_idate', 'poli_lidate', 'stoc_brof']
const STOCK_IN_TEMPLATE_SAMPLE_ROW = ['72206550', '60199730', '60199730', '01/01/2020', '01/01/2021', 'PRU']

const CHANGE_OFFICE_TEMPLATE_HEADERS = ['#Policy_No', 'Issued_Date', 'New office']
const CHANGE_OFFICE_TEMPLATE_SAMPLE_ROW = ['72206550', '01/01/2020', 'PRU']

const normalizeHeader = (header: string) =>
  header
    .replace(/^\uFEFF/, '')
    .replace(/[\u200B-\u200D\u2060]/g, '')
    .trim()
    .replace(/^"(.*)"$/, '$1')
    .trim()
    .toLowerCase()

const normalizeCell = (value: string) =>
  value
    .trim()
    .replace(/^\\"|\\"$/g, '')
    .replace(/^"|"$/g, '')
    .trim()

const a11yProps = (index: number) => {
  return {
    id: `stock-out-tab-${index}`,
    'aria-controls': `stock-out-tabpanel-${index}`
  }
}

const getDelimiter = (headerLine: string) => {
  const delimiters = [',', ';', '\t']
  return delimiters.reduce((selectedDelimiter, delimiter) => {
    const selectedCount = headerLine.split(selectedDelimiter).length
    const delimiterCount = headerLine.split(delimiter).length
    return delimiterCount > selectedCount ? delimiter : selectedDelimiter
  }, ',')
}

const parseCsvFile = (text: string): { headers: string[]; rows: string[][] } => {
  const lines = text.split(/\r\n|\n|\r/).filter((line) => line.trim().length > 0)
  if (lines.length === 0) {
    return { headers: [], rows: [] }
  }

  const delimiter = getDelimiter(lines[0])
  const headers = lines[0].split(delimiter).map(normalizeHeader)
  const rows = lines.slice(1).map((line) => line.split(delimiter).map(normalizeCell))
  return { headers, rows }
}

const validateUpload = (headers: string[], rows: string[][], expectedHeaders: string[]): string | null => {
  const hasExpectedHeaders =
    headers.length === expectedHeaders.length &&
    headers.every((header, index) => header === normalizeHeader(expectedHeaders[index]))
  if (!hasExpectedHeaders) {
    return `Tệp không đúng định dạng. Cột tiêu đề phải là: ${expectedHeaders.join(', ')}.`
  }

  const hasInvalidRow = rows.some((row) => row.length !== expectedHeaders.length || row.some((cell) => !cell))
  if (hasInvalidRow) {
    return 'Dữ liệu không được để trống ở bất kỳ cột nào.'
  }

  return null
}

export default function PDUploadScreen({ route, navigation }: Props) {
  const [value, setValue] = React.useState(route.params?.tab === 'otherOffices' ? 1 : 0)
  const [fileName, setFileName] = React.useState<string | null>(null)
  const [fileContent, setFileContent] = React.useState<string | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [openModal, setOpenModal] = React.useState(false)
  const [title, setTitle] = React.useState('Thông báo')
  const [message, setMessage] = React.useState('')
  const inputRef = React.useRef<HTMLInputElement>(null)
  const permissions = getPermissions()
  const allowUpload = permissions.includes(PolicyDispatchingPermission.Upload)

  React.useEffect(() => {
    setValue(route.params?.tab === 'otherOffices' ? 1 : 0)
  }, [route.params?.tab])

  const resetFile = () => {
    setFileName(null)
    setFileContent(null)
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setValue(newValue)
    navigation.setParams({ tab: newValue === 1 ? 'otherOffices' : 'policyUpload' })
    resetFile()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      resetFile()
      return
    }

    if (!file.name.toLowerCase().endsWith('.csv')) {
      resetFile()
      showResult('Thông báo', 'Chỉ chấp nhận tệp có định dạng .csv.')
      return
    }

    setFileName(file.name)
    const reader = new FileReader()
    reader.onload = () => {
      setFileContent(typeof reader.result === 'string' ? reader.result : null)
    }
    reader.readAsText(file)
  }

  const showResult = (resultTitle: string, resultMessage: string) => {
    setTitle(resultTitle)
    setMessage(resultMessage)
    setOpenModal(true)
    setIsLoading(false)
  }

  const handleSave = () => {
    if (!fileContent) {
      return
    }

    const { headers, rows } = parseCsvFile(fileContent)
    if (rows.length === 0) {
      showResult('Thông báo', 'Tệp không có dữ liệu.')
      return
    }

    const expectedHeaders = value === 0 ? STOCK_IN_TEMPLATE_HEADERS : CHANGE_OFFICE_TEMPLATE_HEADERS
    const validationError = validateUpload(headers, rows, expectedHeaders)
    if (validationError) {
      showResult('Thông báo', validationError)
      return
    }

    setIsLoading(true)

    if (value === 0) {
      const payload: PolicyDispatchingService.UploadStockInPayload[] = rows.map(
        ([contractNumber, agentNumber1, agentNumber2, firstIssueDate, policyIssueDate, officeCode]) => ({
          contractNumber: contractNumber || '',
          agentNumber: agentNumber1 || '',
          policyIssueDate: policyIssueDate || '',
          officeCode: officeCode || ''
        })
      )

      pipe(
        PolicyDispatchingService.uploadStockIn(payload),
        ZIO.tap(() =>
          ZIO.effectTotal(() => {
            showResult('Tải lên hợp đồng', 'Tải lên thành công')
            resetFile()
          })
        ),
        ZIO.catchAll((error) =>
          ZIO.effectTotal(() => {
            console.error('Unable to upload policy stock-in file', error)
            showResult('Tải lên hợp đồng', 'Có lỗi xảy ra. Vui lòng thử lại sau.')
          })
        ),
        ZIO.unsafeRun({})
      )
    } else {
      const payload: PolicyDispatchingService.UploadChangeOfficePayload[] = rows.map(
        ([contractNumber, issuedDate, newOffice]) => ({
          contractNumber: contractNumber || '',
          newOffice: newOffice || ''
        })
      )

      pipe(
        PolicyDispatchingService.uploadChangeOffice(payload),
        ZIO.tap(() =>
          ZIO.effectTotal(() => {
            showResult('Tải lên các văn phòng khác', 'Tải lên thành công')
            resetFile()
          })
        ),
        ZIO.catchAll((error) =>
          ZIO.effectTotal(() => {
            console.error('Unable to upload change-office file', error)
            showResult('Tải lên các văn phòng khác', 'Có lỗi xảy ra. Vui lòng thử lại sau.')
          })
        ),
        ZIO.unsafeRun({})
      )
    }
  }

  const handleDownloadTemplate = () => {
    if (value === 0) {
      exportCSV('PolicyUpload', [STOCK_IN_TEMPLATE_HEADERS, STOCK_IN_TEMPLATE_SAMPLE_ROW])
    } else {
      exportCSV('OtherOffices', [CHANGE_OFFICE_TEMPLATE_HEADERS, CHANGE_OFFICE_TEMPLATE_SAMPLE_ROW])
    }
  }

  return (
    <View style={styles.container}>
      <PolicyDispatchingDialog
        visible={openModal}
        title={title}
        message={message}
        onClose={() => setOpenModal(false)}
      />

      <Text style={styles.title}>{value === 0 ? 'Tải lên hợp đồng' : 'Tải lên các văn phòng khác'}</Text>

      <View style={styles.tabBar}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="stock out tabs"
          TabIndicatorProps={{ style: { display: 'none' } }}
        >
          <Tab
            label="Tải lên hợp đồng"
            {...a11yProps(0)}
            style={value === 0 ? styles.selectedTab : styles.unselectedTab}
          />
          <Tab
            label="Tải lên các văn phòng khác"
            {...a11yProps(1)}
            style={value === 1 ? styles.selectedTab : styles.unselectedTab}
          />
        </Tabs>
      </View>

      <View style={styles.wrapper}>
        <View style={styles.headerRow}>
          <View style={styles.formatText}>
            <Text>File theo định dạng: </Text>
            <span style={styles.formatLink} onClick={handleDownloadTemplate}>
              {value === 0 ? 'PolicyUpload.csv' : 'OtherOffices.csv'}
            </span>
          </View>
          <Text style={styles.requiredHint}>(* : thông tin phải nhập)</Text>
        </View>

        <View style={styles.formBlock}>
          <label htmlFor={'upload-file'} style={styles.label}>
            <span style={styles.requiredMark}>*</span>Chọn file
          </label>

          <View style={styles.fileRow}>
            <label htmlFor={'upload-file'} style={styles.filePickButton}>
              Chọn tệp
            </label>
            <span style={styles.fileName}>{fileName || 'Không tệp nào được chọn'}</span>
            <input
              ref={inputRef}
              id={'upload-file'}
              type="file"
              accept=".csv"
              style={styles.hiddenInput}
              onChange={handleFileChange}
            />
          </View>
        </View>

        <View style={styles.actions}>
          <button type="button" style={styles.resetButton} onClick={resetFile} disabled={isLoading}>
            <span style={styles.mr_5}>
              <assets.PDRevert />
            </span>
            Xóa, làm lại
          </button>
          <button
            type="button"
            style={fileName && !isLoading && allowUpload ? styles.saveButtonActive : styles.saveButton}
            disabled={!fileName || isLoading || !allowUpload}
            onClick={handleSave}
          >
            <span style={styles.mr_5}>
              <assets.PDSave />
            </span>
            Lưu
          </button>
        </View>
      </View>

      {isLoading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#ED1B2E" />
        </View>
      )}
    </View>
  )
}

const styles: Record<string, any> = {
  container: {
    paddingTop: 0,
    paddingRight: 40,
    paddingBottom: 30,
    paddingLeft: 40,
    height: '100%',
    overflow: 'auto'
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '20px'
  },
  tabBar: {
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    marginBottom: '20px'
  },
  tabButton: {
    backgroundColor: 'transparent',
    borderWidth: 0,
    color: '#2b3d5c',
    fontSize: '27px',
    fontWeight: 500,
    paddingTop: 14,
    paddingRight: 22,
    paddingBottom: 14,
    paddingLeft: 22,
    borderRadius: '14px'
  },
  tabButtonActive: {
    backgroundColor: '#2762f3',
    color: '#ffffff',
    boxShadow: '0 1px 2px rgba(39, 98, 243, 0.35)'
  },
  wrapper: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
  },
  headerRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '18px',
    marginBottom: '22px',
    color: '#304567'
  },
  formatText: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap' as const,
    alignItems: 'baseline',
    fontSize: '14px',
    fontStyle: 'italic' as const,
    lineHeight: 1.25,
    marginTop: 8
  },
  formatLink: {
    color: '#e03131',
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
    marginLeft: 4,
    cursor: 'pointer'
  },
  requiredHint: {
    fontSize: '14px',
    fontStyle: 'italic' as const,
    color: '#61718d',
    whiteSpace: 'nowrap' as const
  },
  formBlock: {
    marginBottom: '36px'
  },
  label: {
    display: 'inline-block',
    marginBottom: '12px',
    fontSize: '14px',
    color: '#000000',
    fontWeight: 500,
    cursor: 'pointer' as const
  },
  requiredMark: {
    color: '#e03131',
    marginRight: '2px'
  },
  fileRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '16px'
  },
  filePickButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '24px',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#b9c3d3',
    backgroundColor: '#ffffff',
    color: '#000000',
    fontSize: '14px',
    paddingTop: 10,
    paddingRight: 26,
    paddingBottom: 10,
    paddingLeft: 26,
    cursor: 'pointer' as const
  },
  fileName: {
    color: '#61718d',
    fontSize: '14px'
  },
  hiddenInput: {
    position: 'absolute' as const,
    width: '1px',
    height: '1px',
    opacity: 0,
    pointerEvents: 'none' as const
  },
  actions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: '14px',
    marginBottom: '2px'
  },
  resetButton: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#c3cad7',
    backgroundColor: '#ffffff',
    color: '#12294a',
    borderRadius: '14px',
    paddingTop: 8,
    paddingRight: 16,
    paddingBottom: 8,
    paddingLeft: 16,
    fontSize: '14px',
    fontWeight: 600,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  saveButton: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#d6dbe6',
    backgroundColor: '#d6dbe6',
    color: '#ffffff',
    borderRadius: '14px',
    paddingTop: 8,
    paddingRight: 16,
    paddingBottom: 8,
    paddingLeft: 16,
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'not-allowed',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  saveButtonActive: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#ED1B2E',
    backgroundColor: '#ED1B2E',
    color: '#ffffff',
    borderRadius: '14px',
    paddingTop: 8,
    paddingRight: 16,
    paddingBottom: 8,
    paddingLeft: 16,
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  selectedTab: {
    backgroundColor: '#ED1B2E',
    paddingTop: 4,
    paddingRight: 20,
    paddingBottom: 4,
    paddingLeft: 20,
    color: '#fff',
    fontWeight: 'bold',
    borderRadius: '10px',
    minWidth: 'auto',
    minHeight: 'auto',
    textTransform: 'none' as const
  },
  unselectedTab: {
    paddingTop: 4,
    paddingRight: 20,
    paddingBottom: 4,
    paddingLeft: 20,
    color: '#000',
    minWidth: 'auto',
    minHeight: 'auto',
    textTransform: 'none' as const
  },
  mr_5: {
    marginRight: '5px'
  },
  loadingOverlay: {
    position: 'fixed' as const,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    zIndex: 2
  }
}
